import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import YouTubeFrame from "@/components/YouTubeFrame";
import GooglePlayBadge from "@/components/GooglePlayBadge";
import { CATEGORIES, VIDEOS } from "@/data/videos";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { webPage, breadcrumb } from "@/lib/jsonLd";
import { trackCta } from "@/lib/analytics";

/**
 * /watch — The StoicOs video library.
 *
 *  • Hero with eyebrow / title / lede
 *  • A featured video (rendered large)
 *  • Sticky tab row of category filters
 *  • Grid of remaining videos (or an empty-state per category)
 *
 * Adding a new video = append one entry to /data/videos.js. No UI changes.
 */
export default function Watch() {
  const { t, lang } = useT();
  const [active, setActive] = useState("all");

  const featured = VIDEOS.find((v) => v.featured && v.status === "published");

  const visible = useMemo(() => {
    const others = VIDEOS.filter((v) => !v.featured);
    const byCategory =
      active === "all" ? others : others.filter((v) => v.category === active);
    return [...byCategory].sort(
      (a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)
    );
  }, [active]);

  const filteredFeatured =
    active === "all" || (featured && featured.category === active) ? featured : null;

  useSEO({
    title: lang === "nl" ? "Watch | StoicOs.ai" : "Watch | StoicOs.ai",
    description:
      lang === "nl"
        ? "De videobibliotheek van StoicOs.ai: trailers, Stoïcijnse leermeesters, tutorials en productupdates."
        : "The StoicOs.ai video library: trailers, Stoic teachers, tutorials and product updates.",
    canonical: "https://stoicos.ai/watch",
    jsonLd: [
      webPage({
        name: "Watch",
        description:
          "The StoicOs.ai video library: trailers, Stoic teachers, tutorials and product updates.",
        url: "/watch",
      }),
      breadcrumb([
        { name: "Home", url: "/" },
        { name: "Watch", url: "/watch" },
      ]),
    ],
  });

  return (
    <main data-testid="watch-page" className="relative pt-32 md:pt-40 pb-32">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        {/* breadcrumb */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-fog/70 hover:text-gold transition-colors mb-12"
          data-testid="watch-back"
        >
          <ArrowLeft size={12} strokeWidth={1.5} /> StoicOs · Virtue Guide
        </Link>

        {/* Eyebrow + title */}
        <div className="max-w-3xl">
          <p
            data-testid="watch-eyebrow"
            className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6"
          >
            {t("watch.eyebrow")}
          </p>
          <h1
            data-testid="watch-title"
            className="font-inscription text-[2.25rem] sm:text-5xl md:text-6xl text-bone tracking-[0.06em] uppercase leading-[1.05]"
          >
            {t("watch.title")}
          </h1>
          <p
            data-testid="watch-lede"
            className="mt-8 font-serif italic text-2xl md:text-3xl text-gold tracking-tight leading-tight"
          >
            {t("watch.lede")}
          </p>
          <p
            data-testid="watch-intro"
            className="mt-8 text-bone/85 font-reading text-lg md:text-xl leading-[1.85] max-w-2xl"
          >
            {t("watch.intro")}
          </p>
          <div className="gold-rule w-40 mt-12" />
        </div>

        {/* Featured video */}
        {filteredFeatured && (
          <section data-testid="watch-featured" className="mt-16 md:mt-20">
            <figure className="w-full">
              <YouTubeFrame
                videoId={filteredFeatured.videoId}
                title={t(filteredFeatured.titleKey)}
                testid={`watch-video-${filteredFeatured.videoId}`}
              />
              <figcaption className="mt-6 flex flex-col gap-1.5">
                <span className="text-[10px] tracking-[0.32em] uppercase text-gold/85">
                  {t(filteredFeatured.eyebrowKey)}
                </span>
                <span className="font-serif text-bone text-2xl md:text-3xl tracking-tight">
                  {t(filteredFeatured.titleKey)}
                </span>
                <span className="font-reading text-bone/70 text-base md:text-lg max-w-2xl">
                  {t(filteredFeatured.captionKey)}
                </span>
              </figcaption>
            </figure>

            {/* Availability update — the launch film above stays untouched;
                its "Coming Soon" belongs to the original trailer. */}
            {filteredFeatured.category === "trailer" && (
              <div className="mt-8 border border-gold/25 bg-gold/[0.04] px-6 py-7 md:px-8" data-testid="vg-availability-block">
                <p data-testid="vg-available-note" className="font-reading text-bone/60 text-sm md:text-base leading-relaxed max-w-2xl">
                  {t("coming.available.note")}
                </p>
                <p data-testid="vg-available-line" className="mt-2.5 font-serif text-gold text-xl md:text-2xl leading-snug max-w-2xl">
                  {t("coming.available.line")}
                </p>
                <div className="mt-6 flex flex-col sm:flex-row items-center sm:items-start gap-4">
                  <a
                    href={process.env.REACT_APP_APPSTORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="vg-available-apple-badge"
                    aria-label="Download Virtue Guide on the App Store"
                    className="inline-block transition-transform duration-300 hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                    style={{ lineHeight: 0 }}
                  >
                    <img
                      src={lang === "nl" ? "/badges/app-store-nl.svg" : "/badges/app-store-en.svg"}
                      alt="Download on the App Store"
                      style={{ height: "54px", width: "auto", display: "block" }}
                      draggable={false}
                    />
                  </a>
                  <GooglePlayBadge
                    visibleHeight={54}
                    placement="watch_trailer_availability"
                    testid="vg-available-google-badge"
                  />
                </div>
              </div>
            )}
          </section>
        )}

        {/* Category tabs */}
        <section className="mt-20 md:mt-24" data-testid="watch-tabs">
          <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">
            {t("watch.browseLabel")}
          </p>
          <div
            className="flex flex-wrap gap-2.5"
            role="tablist"
            aria-label={t("watch.browseLabel")}
          >
            {CATEGORIES.map((cat) => {
              const isActive = active === cat.slug;
              return (
                <button
                  key={cat.slug}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`watch-tab-${cat.slug}`}
                  onClick={() => {
                    setActive(cat.slug);
                    trackCta("watch_filter", { category: cat.slug });
                  }}
                  className={`px-4 py-2 text-[11px] tracking-[0.22em] uppercase border transition-colors ${
                    isActive
                      ? "border-gold text-gold bg-gold/5"
                      : "border-white/10 text-bone/75 hover:text-bone hover:border-white/25"
                  }`}
                >
                  {t(cat.labelKey)}
                </button>
              );
            })}
          </div>
        </section>

        {/* Grid of other videos / empty state */}
        <section className="mt-12" data-testid="watch-grid">
          {visible.length === 0 ? (
            <div
              data-testid="watch-empty"
              className="p-10 md:p-14 border border-white/8 bg-ink-elevated/40 text-center"
            >
              <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-gold mb-4">
                <Clock size={14} strokeWidth={1.5} /> {t("watch.empty.eyebrow")}
              </p>
              <p className="font-serif text-2xl md:text-3xl text-bone tracking-tight">
                {t("watch.empty.title")}
              </p>
              <p className="mt-4 max-w-xl mx-auto text-bone/75 font-reading text-base md:text-lg leading-[1.85]">
                {t("watch.empty.body")}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
              {visible.map((v) => (
                <figure key={v.videoId} className="w-full">
                  <YouTubeFrame
                    videoId={v.videoId}
                    title={t(v.titleKey)}
                    testid={`watch-video-${v.videoId}`}
                  />
                  <figcaption className="mt-5 flex flex-col gap-1.5">
                    <span className="text-[10px] tracking-[0.32em] uppercase text-gold/85">
                      {t(v.eyebrowKey)}
                    </span>
                    <span className="font-serif text-bone text-xl md:text-2xl tracking-tight">
                      {t(v.titleKey)}
                    </span>
                    <span className="font-reading text-bone/70 text-sm md:text-base">
                      {t(v.captionKey)}
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
